/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { Bell, Award, FileCheck, X } from 'lucide-react';
import LoadErrorCard from '@components/ui/LoadErrorCard';
import { EmptyState as GenericEmptyState } from '@components/ui/EmptyState';
import { timeAgo } from '@/lib/dueDates';
import type { NotifItem, NotificationsView } from '@hooks/useNotifications';

/*
 * The bell's dropdown, now purely presentational (duplicate-logic finding #27).
 *
 * It used to run its own `Promise.all` over GET /badges/me and
 * GET /certificates/me and carry its own copy of the dismissed-set helpers —
 * the same two requests and the same helper the header had already run, so
 * loading a page and opening the bell cost four requests for two lists. Both
 * now live in `hooks/useNotifications`, which `Header` calls once and passes in
 * here, so the badge count and this list are the same data.
 *
 * The compact relative wording ("5m ago") moved to `lib/dueDates.ts` as
 * `timeAgo`; the admin activity feed's roomier `timeAgoLong` ("5 min ago") is
 * beside it. The two voices are deliberately kept apart — see that module.
 */

interface Props {
  notifications: NotificationsView;
  onClose: () => void;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function PanelHeader({
  hasItems,
  onClearAll,
  onClose,
}: {
  hasItems: boolean;
  onClearAll: () => void;
  onClose: () => void;
}) {
  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-surface-border">
      <h3 className="text-sm font-semibold text-text-primary">Notifications</h3>
      <div className="flex items-center gap-2">
        {hasItems && (
          <button
            onClick={onClearAll}
            className="text-xs text-text-muted hover:text-text-primary transition-colors"
          >
            Clear all
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close notifications"
          className="text-text-muted hover:text-text-primary transition-colors"
        >
          <X size={14} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <GenericEmptyState
      variant="no-data"
      size="sm"
      framed={false}
      icon={<Bell aria-hidden />}
      title="No notifications yet"
      description="Complete courses to earn badges & certificates."
    />
  );
}

function NotifIcon({ item }: { item: NotifItem }) {
  return (
    <div
      className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
        item.type === 'badge' ? 'bg-yellow-500/10' : 'bg-primary/10'
      }`}
    >
      {item.type === 'badge' ? (
        <Award size={14} aria-hidden="true" className="text-yellow-400" />
      ) : (
        <FileCheck size={14} aria-hidden="true" className="text-primary" />
      )}
    </div>
  );
}

function NotifRow({ item, onDismiss }: { item: NotifItem; onDismiss: (id: string) => void }) {
  return (
    <div className="flex gap-3 px-4 py-3 hover:bg-surface-border/40 border-b border-surface-border/50 last:border-0 transition-colors group">
      <NotifIcon item={item} />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-text-primary leading-snug">{item.title}</p>
        {item.subtitle && (
          <p className="text-xs text-text-muted truncate mt-0.5">{item.subtitle}</p>
        )}
      </div>
      <span className="text-xs text-text-muted shrink-0 pt-0.5">{timeAgo(item.issuedAt)}</span>
      <button
        type="button"
        onClick={() => onDismiss(item.id)}
        className="text-text-muted hover:text-text-primary opacity-0 group-hover:opacity-100 transition-opacity shrink-0 pt-0.5"
        title="Dismiss"
        aria-label={`Dismiss notification: ${item.title}${item.subtitle ? `, ${item.subtitle}` : ''}`}
      >
        <X size={12} aria-hidden="true" />
      </button>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function NotificationsPanel({ notifications, onClose }: Props) {
  const { items, loading, failed, dismiss, clearAll, refetch } = notifications;

  return (
    <div className="absolute right-0 top-full mt-2 w-80 bg-surface-card border border-surface-border rounded-xl shadow-2xl z-50">
      <PanelHeader hasItems={items.length > 0} onClearAll={clearAll} onClose={onClose} />

      {/* Body */}
      <div className="max-h-80 overflow-y-auto">
        {loading ? (
          <div className="p-6 text-center text-xs text-text-muted animate-pulse">Loading…</div>
        ) : failed ? (
          <LoadErrorCard
            className="border-0 shadow-none"
            title="Could not load your notifications."
            hint={null}
            onRetry={refetch}
          />
        ) : items.length === 0 ? (
          <EmptyState />
        ) : (
          items.map((item) => <NotifRow key={item.id} item={item} onDismiss={dismiss} />)
        )}
      </div>
    </div>
  );
}
